import React from 'react';
import type { EmployerProfileDetail } from '../../lib/engagementService';
import { boostRealOffer, boostOfferApplications } from '../../lib/engagementService';
import type { SeedCampaign } from '../../lib/seedService';
import BoostPanel from './BoostPanel';

interface Props {
  detail: EmployerProfileDetail;
  campaigns: SeedCampaign[];
  onBoostComplete: (label: string, campaign: string, count: number) => void;
}

const EmployerCard: React.FC<Props> = ({ detail, campaigns, onBoostComplete }) => {
  const { firstName, lastName, companyName, city, email, isSeed, seedTag, offers, stats } = detail;
  const displayName = companyName || `${firstName} ${lastName}`.trim() || 'Sans nom';

  return (
    <div className="bg-white rounded-xl border-2 border-purple-200 p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-lg">
          {(companyName?.[0] || firstName?.[0] || '?').toUpperCase()}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">{displayName}</h2>
            {isSeed && <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded">SEED</span>}
          </div>
          <p className="text-sm text-gray-500">{city} &middot; {email}</p>
          {seedTag && <p className="text-xs text-gray-400">Campagne : {seedTag}</p>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {([
          { label: 'Offres', value: stats.totalOffers, color: 'text-purple-600 bg-purple-50' },
          { label: 'Candidatures reçues', value: stats.totalApplicationsReceived, color: 'text-green-600 bg-green-50' },
          { label: 'Vues profil faites', value: stats.profileViewsMade, color: 'text-blue-600 bg-blue-50' },
          { label: 'Sélections faites', value: stats.selectionsMade, color: 'text-amber-600 bg-amber-50' },
        ] as const).map((s) => (
          <div key={s.label} className={`rounded-lg p-3 ${s.color}`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs font-medium mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Offers */}
      {offers.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Offres ({offers.length})</h3>
          <div className="space-y-3">
            {offers.map((o) => (
              <div key={o.id} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-800">{o.positionName}</span>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-gray-400">{o.city} &middot; {o.contractType}</span>
                    <span className="text-indigo-600 font-medium">{o.swipes} swipes</span>
                    <span className="text-amber-600 font-medium">{o.favorites} favoris</span>
                    <span className="text-green-600 font-medium">{o.applications} candidatures</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <BoostPanel
                    campaigns={campaigns}
                    label="+ Swipes"
                    onBoost={(c, n, s) =>
                      boostRealOffer({ campaignTag: c, targetOfferId: o.id, action: 'swipe', count: n, spreadDays: s })
                    }
                    onComplete={(c, n) => onBoostComplete(`Swipes ${o.positionName}`, c, n)}
                  />
                  <BoostPanel
                    campaigns={campaigns}
                    label="+ Favoris"
                    onBoost={(c, n, s) =>
                      boostRealOffer({ campaignTag: c, targetOfferId: o.id, action: 'favorite', count: n, spreadDays: s })
                    }
                    onComplete={(c, n) => onBoostComplete(`Favoris ${o.positionName}`, c, n)}
                  />
                  <BoostPanel
                    campaigns={campaigns}
                    label="+ Candidatures"
                    onBoost={(c, n, s) =>
                      boostOfferApplications({ campaignTag: c, offerId: o.id, count: n, spreadDays: s })
                    }
                    onComplete={(c, n) => onBoostComplete(`Candidatures ${o.positionName}`, c, n)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {offers.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-4">Aucune offre publiée</p>
      )}
    </div>
  );
};

export default EmployerCard;
